'use client';

import { c as _c } from "react/compiler-runtime";
import type { HeroSlots } from './useHeroSlots';
import type { HeroConnection, HeroState } from './utils';
import type { GaugeTemplateCard } from '@effektify/engine';
import type React from 'react';
import { useCardState, useGaugeProps } from '@effektify/engine';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { FlexColumn, FlexRow } from 'src/components/Atoms/FlexBox';
import { GoalEditButton } from 'src/components/Molecules/GoalEditButton';
import ConnectionStatusChip from './ConnectionStatusChip';
import HeroGauge from './HeroGauge';
import HeroHeader from './HeroHeader';
import HeroKpiStat from './HeroKpiStat';
import { easeOutExpo, GoalDialogTransition, HERO_MOTION, IN_VIEW, useCountUp } from './motion';
import { fillTextPlaceholders, getGoalProgress, getProgressVerdict, toNumber } from './utils';
import useFeatures from 'src/hooks/useFeatures';
import { KNOWN_FEATURES } from 'src/constants/features';
interface HeroSectionProps {
  slots: HeroSlots;
  heroState: HeroState;
  connections: HeroConnection[];
  platformNames: string[];
  firstName: string;
  teamName: string;
  connectionsUrl: string;
  onNavigate: (url: string) => void;
  isDataSettled: boolean;
}
interface GaugeSummary {
  goalLabel: string | null;
  progress: number | null;
  verdict: string | null;
  description?: string;
  editSlot?: React.ReactNode;
}
const Grid = styled('div')(({
  theme
}) => ({
  display: 'grid',
  gap: theme.sizes.spacing['10'],
  alignItems: 'center',
  gridTemplateColumns: '1fr',
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: '1.15fr 1fr'
  }
}));
const HeroSectionLayout: React.FC<HeroSectionProps & {
  gauge: GaugeSummary | null;
  gaugeState?: HeroState;
}> = t0 => {
  const $ = _c(52);
  const {
    slots,
    heroState,
    connections,
    platformNames,
    firstName,
    teamName,
    connectionsUrl,
    onNavigate,
    isDataSettled,
    gauge,
    gaugeState: t1
  } = t0;
  const gaugeState = t1 === undefined ? heroState : t1;
  const ref = useRef(null);
  const isInView = useInView(ref, IN_VIEW);
  const isPlaying = isInView && gaugeState === "normal";
  const progress = gauge?.progress ?? null;
  const displayProgress = useCountUp(progress, isPlaying);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      durationMs: HERO_MOTION.gaugeArc.duration * 1000,
      ease: easeOutExpo
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const arcProgress = useCountUp(progress, isPlaying, t2);
  let t3;
  if ($[1] !== connections) {
    t3 = connections.filter(_temp);
    $[1] = connections;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const activeCount = t3.length;
  const {
    y,
    duration
  } = HERO_MOTION.section;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 0,
      y
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== isInView) {
    t5 = isInView ? {
      opacity: 1,
      y: 0
    } : undefined;
    $[4] = isInView;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      duration,
      ease: "easeOut"
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = gauge?.goalLabel ?? null;
  let t8;
  if ($[7] !== connectionsUrl || $[8] !== onNavigate) {
    t8 = () => onNavigate(connectionsUrl);
    $[7] = connectionsUrl;
    $[8] = onNavigate;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = `${activeCount} of ${connections.length} platforms connected`;
  let t10;
  if ($[10] !== t9) {
    t10 = <Typography variant="body-xs" sx={_temp4}>{t9}</Typography>;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== connections || $[13] !== connectionsUrl || $[14] !== onNavigate) {
    let t12;
    if ($[16] !== connectionsUrl || $[17] !== onNavigate) {
      t12 = connection_0 => <ConnectionStatusChip key={connection_0.id} connection={connection_0} connectionsUrl={connectionsUrl} onNavigate={onNavigate} />;
      $[16] = connectionsUrl;
      $[17] = onNavigate;
      $[18] = t12;
    } else {
      t12 = $[18];
    }
    t11 = connections.map(t12);
    $[12] = connections;
    $[13] = connectionsUrl;
    $[14] = onNavigate;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[19] !== t11) {
    t12 = <FlexRow sx={_temp5}>{t11}</FlexRow>;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t10 || $[22] !== t12) {
    t13 = <FlexColumn sx={_temp3}>{t10}{t12}</FlexColumn>;
    $[21] = t10;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== displayProgress || $[25] !== firstName || $[26] !== gaugeState || $[27] !== platformNames || $[28] !== t13 || $[29] !== t7 || $[30] !== t8 || $[31] !== teamName) {
    t14 = <HeroHeader heroState={gaugeState} firstName={firstName} teamName={teamName} goalLabel={t7} displayProgress={displayProgress} platformNames={platformNames} onConnect={t8} connectionsSlot={t13} />;
    $[24] = displayProgress;
    $[25] = firstName;
    $[26] = gaugeState;
    $[27] = platformNames;
    $[28] = t13;
    $[29] = t7;
    $[30] = t8;
    $[31] = teamName;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== heroState || $[34] !== isDataSettled || $[35] !== isInView || $[36] !== slots.kpis) {
    t15 = slots.kpis.length > 0 && <FlexRow sx={_temp6}>{slots.kpis.map((card, index) => <HeroKpiStat key={card.id ?? `hero-kpi-${index}`} card={card} index={index} heroState={heroState} isInView={isInView} isDataSettled={isDataSettled} />)}</FlexRow>;
    $[33] = heroState;
    $[34] = isDataSettled;
    $[35] = isInView;
    $[36] = slots.kpis;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15) {
    t16 = <FlexColumn sx={_temp2}>{t14}{t15}</FlexColumn>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== arcProgress || $[42] !== displayProgress || $[43] !== gauge || $[44] !== gaugeState) {
    t17 = gauge && <HeroGauge heroState={gaugeState} arcProgress={arcProgress ?? 0} displayProgress={displayProgress} verdict={gauge.verdict} description={gauge.description} editSlot={gauge.editSlot} />;
    $[41] = arcProgress;
    $[42] = displayProgress;
    $[43] = gauge;
    $[44] = gaugeState;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t16 || $[47] !== t17) {
    t18 = <Grid>{t16}{t17}</Grid>;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t18 || $[50] !== t5) {
    t19 = <motion.section ref={ref} initial={t4} animate={t5} transition={t6}>{t18}</motion.section>;
    $[49] = t18;
    $[50] = t5;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  return t19;
};
const HeroSectionWithGauge: React.FC<HeroSectionProps & {
  card: GaugeTemplateCard;
}> = t0 => {
  const $ = _c(31);
  let card;
  let props;
  if ($[0] !== t0) {
    ({
      card,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = card;
    $[2] = props;
  } else {
    card = $[1];
    props = $[2];
  }
  const {
    data,
    shouldRender
  } = useCardState(card);
  const {
    title,
    footer,
    chart,
    goalType
  } = useGaugeProps(card, data);
  const {
    isEnabled
  } = useFeatures();
  const isGaugePending = toNumber(chart.data?.value) === null && !props.isDataSettled;
  if (!shouldRender) {
    let t1;
    if ($[3] !== props) {
      t1 = <HeroSectionLayout {...props} gauge={null} />;
      $[3] = props;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  let t2;
  let t3;
  if ($[5] !== chart.data || $[6] !== chart.goal?.benchmark || $[7] !== chart.goal?.goal || $[8] !== title) {
    const progress = getGoalProgress(chart.data, chart.goal?.goal);
    if ($[12] !== title) {
      t1 = title?.text ? fillTextPlaceholders(title.text) : null;
      $[12] = title;
      $[13] = t1;
    } else {
      t1 = $[13];
    }
    t2 = progress;
    t3 = getProgressVerdict({
      value: chart.data?.value,
      benchmark: chart.goal?.benchmark,
      progress
    });
    $[5] = chart.data;
    $[6] = chart.goal?.benchmark;
    $[7] = chart.goal?.goal;
    $[8] = title;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
  }
  const t4 = footer.text || undefined;
  let t5;
  if ($[14] !== chart.data?.currency || $[15] !== chart.data?.type || $[16] !== chart.goal?.description || $[17] !== chart.goal?.goal || $[18] !== goalType || $[19] !== isEnabled) {
    t5 = goalType && isEnabled(KNOWN_FEATURES.ALLOW_GOAL_EDITING) ? <GoalEditButton goalType={goalType} goal={chart.goal?.goal} description={chart.goal?.description} valueType={chart.data?.type ?? "integer"} currency={chart.data?.currency} dialogTransition={GoalDialogTransition} /> : undefined;
    $[14] = chart.data?.currency;
    $[15] = chart.data?.type;
    $[16] = chart.goal?.description;
    $[17] = chart.goal?.goal;
    $[18] = goalType;
    $[19] = isEnabled;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t1 || $[22] !== t2 || $[23] !== t3 || $[24] !== t4 || $[25] !== t5) {
    t6 = {
      goalLabel: t1,
      progress: t2,
      verdict: t3,
      description: t4,
      editSlot: t5
    };
    $[21] = t1;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const gauge = t6;
  const t7 = props.heroState === "normal" && isGaugePending ? "loading" : props.heroState;
  let t8;
  if ($[27] !== gauge || $[28] !== props || $[29] !== t7) {
    t8 = <HeroSectionLayout {...props} gauge={gauge} gaugeState={t7} />;
    $[27] = gauge;
    $[28] = props;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
};
const HeroSection: React.FC<HeroSectionProps> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = props.slots.gauge ? <HeroSectionWithGauge {...props} card={props.slots.gauge} /> : <HeroSectionLayout {...props} gauge={null} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export default HeroSection;
function _temp(connection) {
  return connection.status === "active";
}
function _temp2(theme) {
  return {
    gap: theme.sizes.spacing["12"]
  };
}
function _temp3(theme_0) {
  return {
    gap: theme_0.sizes.spacing["05"]
  };
}
function _temp4(theme_1) {
  return {
    color: theme_1.colors.text.tertiary
  };
}
function _temp5(theme_2) {
  return {
    flexWrap: "wrap",
    gap: theme_2.sizes.spacing["04"]
  };
}
function _temp6(theme_3) {
  return {
    flexWrap: "wrap",
    columnGap: theme_3.sizes.spacing["09"],
    rowGap: theme_3.sizes.spacing["08"]
  };
}